(function () {
  'use strict';

  const selectors = {
    page: '.product__page',
    formWrapper: '[data-form-wrapper]',
    headerSticky: '[data-header-sticky]',
    mediaList: '[data-product-media-list]',
    productImages: 'product-images',
  };

  const classes = {
    sticky: 'is-sticky',
  };

  if (customElements.get('product-sticky')) return;

  customElements.define(
    'product-sticky',
    class ProductSticky extends HTMLElement {
      constructor() {
        super();

        this.formWrapper = this.querySelector(selectors.formWrapper);
        this.productImages = this.querySelector(selectors.productImages);
        this.stickyTarget = null;
        this.stickyScrollTop = 0;
        this.scrollLastPosition = 0;
        this.stickyDefaultTop = 0;
        this.currentPoint = 0;
        this.defaultTopBottomSpacings = 30;
        this.scrollTopPosition = window.scrollY;
        this.scrollDirectionDown = true;
        this.requestAnimationSticky = null;
        this.stickyFormLoad = true;
        this.stickyFormLastHeight = null;
        this.onChangeCounter = 0;
        this.isScrollBound = false;

        this.scrollEvent = (event) => this.scrollEvents(event);
        this.resizeEvent = () => this.resizeEvents();
        this.stickyFormEvent = (event) => this.stickyFormEvents(event);
        this.mediaChangeEvent = (event) => this.mediaChangeEvents(event);
      }

      connectedCallback() {
        this.stickyScrollCheck();
        this.bindChangeEvents();
        this.syncScrollEvents();
        document.addEventListener('theme:resize', this.resizeEvent);

        if (theme.settings.productPageSticky) {
          this.requestAnimationSticky = requestAnimationFrame(() => this.calculateStickyPosition());
        }
      }

      bindChangeEvents() {
        this.formWrapper?.addEventListener('theme:form:sticky', this.stickyFormEvent);
        this.productImages?.addEventListener('theme:media:select', this.mediaChangeEvent);
      }

      unbindChangeEvents() {
        this.formWrapper?.removeEventListener('theme:form:sticky', this.stickyFormEvent);
        this.productImages?.removeEventListener('theme:media:select', this.mediaChangeEvent);
      }

      syncScrollEvents() {
        if (theme.settings.productPageSticky && !this.isScrollBound) {
          document.addEventListener('theme:scroll', this.scrollEvent);
          this.isScrollBound = true;
        }

        if (!theme.settings.productPageSticky && this.isScrollBound) {
          document.removeEventListener('theme:scroll', this.scrollEvent);
          this.isScrollBound = false;
        }
      }

      stickyFormEvents(event) {
        this.refreshStickyTarget(event);
      }

      mediaChangeEvents(event) {
        requestAnimationFrame(() => this.refreshStickyTarget(event));
      }

      scrollEvents(event) {
        this.scrollTopPosition = event.detail.position;
        this.scrollDirectionDown = event.detail.down;

        if (!this.requestAnimationSticky) {
          this.requestAnimationSticky = requestAnimationFrame(() => this.calculateStickyPosition());
        }
      }

      resizeEvents() {
        this.refreshStickyTarget();
      }

      refreshStickyTarget(event = null) {
        this.removeAnimationFrameSticky();
        this.stickyScrollCheck();
        this.syncScrollEvents();

        if (!theme.settings.productPageSticky) return;

        this.stickyFormLoad = true;
        this.scrollTopPosition = window.scrollY;
        this.requestAnimationSticky = requestAnimationFrame(() => this.calculateStickyPosition(event));
      }

      clearStickyTarget() {
        this.formWrapper?.classList.remove(classes.sticky);
        this.productImages?.classList.remove(classes.sticky);
        this.stickyTarget = null;
        theme.settings.productPageSticky = false;
      }

      getMediaHeight(mediaList) {
        const cssHeight = parseFloat(this.productImages?.style.getPropertyValue('--height'));
        return cssHeight || mediaList?.offsetHeight || this.productImages?.offsetHeight || 0;
      }

      stickyScrollCheck() {
        if (!this.formWrapper) return;

        this.productImages = this.querySelector(selectors.productImages);
        this.clearStickyTarget();

        if (theme.isMobile) return;

        const formWrapper = this.querySelector(`${selectors.page} ${selectors.formWrapper}`);
        const mediaList = this.querySelector(selectors.mediaList);
        const productImages = this.productImages;

        if (!formWrapper || !mediaList || !productImages) return;

        const mediaHeight = this.getMediaHeight(mediaList);
        const formHeight = formWrapper.offsetHeight;

        if (!mediaHeight || !formHeight) return;

        if (formHeight < mediaHeight) {
          this.stickyTarget = this.formWrapper;
        } else if (mediaHeight < formHeight) {
          this.stickyTarget = productImages;
        }

        if (this.stickyTarget) {
          theme.settings.productPageSticky = true;
          this.stickyTarget.classList.add(classes.sticky);
        }
      }

      getStickyTargetHeight() {
        if (this.stickyTarget === this.productImages) {
          return this.getMediaHeight(this.querySelector(selectors.mediaList));
        }

        return this.stickyTarget?.offsetHeight || 0;
      }

      calculateStickyPosition(event = null) {
        const stickyTarget = this.stickyTarget;

        if (!stickyTarget) {
          this.removeAnimationFrameSticky();
          return;
        }

        if (document.documentElement.hasAttribute('data-scroll-locked')) {
          this.removeAnimationFrameSticky();
          return;
        }

        const isAccordion = Boolean(event?.detail?.element === 'accordion');
        const stickyTargetHeight = this.getStickyTargetHeight();
        const stickyLimit = window.innerHeight - stickyTargetHeight - this.defaultTopBottomSpacings;
        const scrollDifference = Math.abs(this.scrollTopPosition - this.scrollLastPosition);

        if (this.scrollDirectionDown) {
          this.stickyScrollTop -= scrollDifference;
        } else {
          this.stickyScrollTop += scrollDifference;
        }

        if (this.stickyFormLoad) {
          if (document.querySelector(selectors.headerSticky)) {
            const {headerHeight} = window.theme.readHeights();
            this.stickyDefaultTop = headerHeight;
          } else {
            this.stickyDefaultTop = this.defaultTopBottomSpacings;
          }

          this.stickyScrollTop = this.stickyDefaultTop;
        }

        this.stickyScrollTop = Math.min(Math.max(this.stickyScrollTop, stickyLimit), this.stickyDefaultTop);

        const pointDifference = this.stickyScrollTop - this.currentPoint;
        this.currentPoint = this.stickyFormLoad ? this.stickyScrollTop : this.currentPoint + 0.5 * pointDifference;

        stickyTarget.style.setProperty('--sticky-top', `${this.currentPoint}px`);

        this.scrollLastPosition = this.scrollTopPosition;
        this.stickyFormLoad = false;

        const shouldKeepAnimating =
          (isAccordion && this.onChangeCounter <= 10) ||
          (isAccordion && this.stickyFormLastHeight !== stickyTargetHeight) ||
          (this.stickyScrollTop !== this.currentPoint && this.requestAnimationSticky);

        if (shouldKeepAnimating) {
          if (isAccordion) {
            this.onChangeCounter += 1;
          }

          if (isAccordion && this.stickyFormLastHeight !== stickyTargetHeight) {
            this.onChangeCounter = 11;
          }

          this.requestAnimationSticky = requestAnimationFrame(() => this.calculateStickyPosition(event));
        } else if (this.requestAnimationSticky) {
          this.removeAnimationFrameSticky();
        }

        this.stickyFormLastHeight = stickyTargetHeight;
      }

      removeAnimationFrameSticky() {
        if (!this.requestAnimationSticky) return;

        cancelAnimationFrame(this.requestAnimationSticky);
        this.requestAnimationSticky = null;
        this.onChangeCounter = 0;
      }

      disconnectedCallback() {
        document.removeEventListener('theme:resize', this.resizeEvent);
        document.removeEventListener('theme:scroll', this.scrollEvent);
        this.unbindChangeEvents();
      }
    }
  );
})();
